import { initialUsers, initialPages, initialPosts, initialCurriculum, initialSubmissions, initialSettings } from "../data/initialData.js";
import { hashPassword, verifyPassword } from "../utils/crypto.js";
const STORAGE_KEYS = {
  AUTH: "bmm_auth_session",
  LANG: "bmm_site_lang"
};
let inMemorySettings = { ...initialSettings };
let inMemoryUsers = [...initialUsers];
let inMemoryPages = [...initialPages];
let inMemoryPosts = [...initialPosts];
let inMemoryCurriculum = [...initialCurriculum];
let inMemorySubmissions = [...initialSubmissions];
function safeGet(key, fallback) {
  try {
    const item = localStorage.getItem(key);
    if (!item) return fallback;
    return JSON.parse(item);
  } catch (e) {
    return fallback;
  }
}
function safeSet(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.warn(`Error writing ${key} to localStorage`, e);
  }
}
function apiPost(url, data) {
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }).catch((e) => console.warn("API Post Error:", e));
}
function apiDelete(url) {
  fetch(url, {
    method: "DELETE"
  }).catch((e) => console.warn("API Delete Error:", e));
}
function apiPatch(url, data) {
  fetch(url, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data)
  }).catch((e) => console.warn("API Patch Error:", e));
}
export const StorageService = {
  // Sync memory cache with MySQL response
  setMemorySettings(data) {
    if (data) inMemorySettings = data;
  },
  setMemoryUsers(data) {
    if (Array.isArray(data)) inMemoryUsers = data;
  },
  setMemoryPages(data) {
    if (Array.isArray(data)) inMemoryPages = data;
  },
  setMemoryPosts(data) {
    if (Array.isArray(data)) inMemoryPosts = data;
  },
  setMemoryCurriculum(data) {
    if (Array.isArray(data)) inMemoryCurriculum = data;
  },
  setMemorySubmissions(data) {
    if (Array.isArray(data)) inMemorySubmissions = data;
  },
  // Settings
  getSettings() {
    return inMemorySettings;
  },
  saveSettings(settings) {
    inMemorySettings = settings;
    apiPost("/api/settings", settings);
  },
  // Users & RBAC
  getUsers() {
    return inMemoryUsers;
  },
  saveUser(user) {
    let targetUser;
    if (user.id) {
      const idx = inMemoryUsers.findIndex((u) => u.id === user.id);
      if (idx !== -1) {
        inMemoryUsers[idx] = { ...inMemoryUsers[idx], ...user };
        targetUser = inMemoryUsers[idx];
        apiPost("/api/users", targetUser);
        return targetUser;
      }
    }
    targetUser = {
      id: `usr-${Date.now()}`,
      name: user.name,
      email: user.email,
      role: user.role,
      password_hash: user.password_hash || "$2y$10$qO0rOQ5byMi.Ye4oKoEa3RpLwT6qUuLp6r6E6q2ZfK7890123",
      hash_algorithm: "bcrypt",
      status: user.status || "locked",
      created_at: (/* @__PURE__ */ new Date()).toISOString().replace("T", " ").substring(0, 19),
      avatar: user.avatar || "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
    };
    inMemoryUsers.push(targetUser);
    apiPost("/api/users", targetUser);
    return targetUser;
  },
  deleteUser(userId) {
    const filtered = inMemoryUsers.filter((u) => u.id !== userId);
    if (filtered.length === inMemoryUsers.length) return false;
    inMemoryUsers = filtered;
    apiDelete("/api/users/" + userId);
    return true;
  },
  // Pages
  getPages() {
    return inMemoryPages;
  },
  getPageBySlug(slug) {
    return this.getPages().find((p) => p.slug === slug);
  },
  savePage(page) {
    const idx = inMemoryPages.findIndex((p) => p.id === page.id);
    let updatedPage = page;
    if (idx !== -1) {
      updatedPage = { ...page, updated_at: (/* @__PURE__ */ new Date()).toISOString().replace("T", " ").substring(0, 19) };
      inMemoryPages[idx] = updatedPage;
    } else {
      inMemoryPages.push(page);
    }
    apiPost("/api/pages", updatedPage);
  },
  // Blog Posts
  getPosts() {
    return inMemoryPosts;
  },
  getPostBySlug(slug) {
    return this.getPosts().find((p) => p.slug === slug);
  },
  savePost(post) {
    const now = (/* @__PURE__ */ new Date()).toISOString().replace("T", " ").substring(0, 19);
    let coverImg = post.cover_image || "/src/assets/images/madrasah_classroom_study_1790844625777.jpg";
    if (post.media && post.media.length > 0) {
      const primary = post.media.find((m) => m.is_primary && m.type === "image") || post.media.find((m) => m.type === "image");
      if (primary) {
        coverImg = primary.url;
      }
    }
    if (post.id) {
      const idx = inMemoryPosts.findIndex((p) => p.id === post.id);
      if (idx !== -1) {
        inMemoryPosts[idx] = {
          ...inMemoryPosts[idx],
          ...post,
          cover_image: coverImg,
          media: post.media || inMemoryPosts[idx].media || [],
          video_url: post.video_url !== void 0 ? post.video_url : inMemoryPosts[idx].video_url,
          updated_at: now
        };
        apiPost("/api/posts", inMemoryPosts[idx]);
        return inMemoryPosts[idx];
      }
    }
    const newPost = {
      id: `post-${Date.now()}`,
      slug: post.slug || post.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, ""),
      title: post.title,
      title_bn: post.title_bn || post.title,
      category: post.category,
      author_id: post.author_id || "usr-1",
      author_name: post.author_name || "Admin",
      excerpt: post.excerpt || "",
      excerpt_bn: post.excerpt_bn || "",
      content: post.content || "",
      content_bn: post.content_bn || "",
      cover_image: coverImg,
      media: post.media || [],
      video_url: post.video_url || "",
      status: post.status || "published",
      views: post.views || 0,
      read_time: post.read_time || "3 min read",
      created_at: now,
      updated_at: now
    };
    inMemoryPosts.unshift(newPost);
    apiPost("/api/posts", newPost);
    return newPost;
  },
  deletePost(postId) {
    const filtered = inMemoryPosts.filter((p) => p.id !== postId);
    if (filtered.length === inMemoryPosts.length) return false;
    inMemoryPosts = filtered;
    apiDelete("/api/posts/" + postId);
    return true;
  },
  incrementPostViews(postId) {
    const p = inMemoryPosts.find((item) => item.id === postId);
    if (p) {
      p.views = (p.views || 0) + 1;
      apiPost("/api/posts", p);
    }
  },
  // Curriculum
  getCurriculum() {
    return inMemoryCurriculum;
  },
  saveCurriculumItem(item) {
    if (item.id) {
      const idx = inMemoryCurriculum.findIndex((c) => c.id === item.id);
      if (idx !== -1) {
        inMemoryCurriculum[idx] = { ...inMemoryCurriculum[idx], ...item };
        apiPost("/api/curriculum", inMemoryCurriculum[idx]);
        return inMemoryCurriculum[idx];
      }
    }
    const newItem = {
      id: `cur-${Date.now()}`,
      class_name: item.class_name,
      class_name_bn: item.class_name_bn || item.class_name,
      age_group: item.age_group || "5 - 10 Years",
      duration: item.duration || "1 Year",
      description: item.description || "",
      description_bn: item.description_bn || "",
      subjects: item.subjects || ["Bangla", "Arabic", "English", "Math"],
      subjects_bn: item.subjects_bn || ["বাংলা", "আরবি", "ইংরেজি", "গণিত"],
      features: item.features || [],
      schedule: item.schedule || "8:00 AM - 1:00 PM",
      is_active: item.is_active !== void 0 ? item.is_active : true,
      order: inMemoryCurriculum.length + 1
    };
    inMemoryCurriculum.push(newItem);
    apiPost("/api/curriculum", newItem);
    return newItem;
  },
  deleteCurriculumItem(id) {
    const filtered = inMemoryCurriculum.filter((c) => c.id !== id);
    if (filtered.length === inMemoryCurriculum.length) return false;
    inMemoryCurriculum = filtered;
    apiDelete("/api/curriculum/" + id);
    return true;
  },
  // Submissions (Contacts & Admissions)
  getSubmissions() {
    return inMemorySubmissions;
  },
  saveSubmission(submission) {
    const newSub = {
      ...submission,
      id: `sub-${Date.now()}`,
      status: submission.status || "new",
      created_at: (/* @__PURE__ */ new Date()).toISOString().replace("T", " ").substring(0, 19)
    };
    inMemorySubmissions.unshift(newSub);
    apiPost("/api/submissions", newSub);
    return newSub;
  },
  updateSubmissionStatus(id, status, admin_notes) {
    const sub = inMemorySubmissions.find((s) => s.id === id);
    if (!sub) return false;
    sub.status = status;
    if (admin_notes !== void 0) {
      sub.admin_notes = admin_notes;
    }
    apiPatch("/api/submissions/" + id, { status, admin_notes });
    return true;
  },
  deleteSubmission(id) {
    const filtered = inMemorySubmissions.filter((s) => s.id !== id);
    if (filtered.length === inMemorySubmissions.length) return false;
    inMemorySubmissions = filtered;
    apiDelete("/api/submissions/" + id);
    return true;
  },
  // Auth Session (Session only)
  getCurrentUser() {
    return safeGet(STORAGE_KEYS.AUTH, null);
  },
  login(email, password) {
    const user = inMemoryUsers.find((u) => u.email.toLowerCase() === email.trim().toLowerCase());
    if (!user) {
      return { success: false, error: "User email not found." };
    }
    if (user.status === "locked" || user.status === "pending") {
      return { success: false, error: "Your staff account is locked or pending super admin approval." };
    }
    if (user.status === "suspended") {
      return { success: false, error: "Your staff account has been suspended." };
    }
    const isValid = verifyPassword(password, user.password_hash || "");
    if (!isValid) {
      return { success: false, error: "Invalid password." };
    }
    this.setCurrentUser(user);
    return { success: true, user };
  },
  logout() {
    this.setCurrentUser(null);
  },
  approveUser(userId) {
    const user = inMemoryUsers.find((u) => u.id === userId);
    if (!user) return false;
    user.status = "active";
    apiPost("/api/users", user);
    return true;
  },
  lockUser(userId) {
    const user = inMemoryUsers.find((u) => u.id === userId);
    if (!user) return false;
    user.status = "locked";
    apiPost("/api/users", user);
    return true;
  },
  resetUserPassword(userId, newPass) {
    const user = inMemoryUsers.find((u) => u.id === userId);
    if (!user) return false;
    user.password_hash = hashPassword(newPass, "bcrypt");
    user.hash_algorithm = "bcrypt";
    apiPost("/api/users", user);
    return true;
  },
  executeSql(sqlStatement) {
    return { success: true, message: "Executed operation against MySQL database." };
  },
  resetDatabase() {
    inMemorySettings = { ...initialSettings };
    inMemoryUsers = [...initialUsers];
    inMemoryPages = [...initialPages];
    inMemoryPosts = [...initialPosts];
    inMemoryCurriculum = [...initialCurriculum];
    inMemorySubmissions = [...initialSubmissions];
    apiPost("/api/settings", initialSettings);
    initialUsers.forEach((u) => apiPost("/api/users", u));
    initialPages.forEach((p) => apiPost("/api/pages", p));
    initialPosts.forEach((po) => apiPost("/api/posts", po));
    initialCurriculum.forEach((c) => apiPost("/api/curriculum", c));
    initialSubmissions.forEach((s) => apiPost("/api/submissions", s));
  },
  generateSqlDump() {
    return `-- Baitul Muqaddas Model Madrasah MySQL Dump
-- Generated: ${(/* @__PURE__ */ new Date()).toISOString()}
`;
  },
  registerUser(data) {
    const normalizedEmail = data.email.trim().toLowerCase();
    if (!data.name.trim()) {
      return { success: false, error: "Full name is required." };
    }
    if (!normalizedEmail || !normalizedEmail.includes("@")) {
      return { success: false, error: "A valid email address is required." };
    }
    if (!data.password || data.password.length < 4) {
      return { success: false, error: "Password must be at least 4 characters long." };
    }
    if (inMemoryUsers.some((u) => u.email.toLowerCase() === normalizedEmail)) {
      return { success: false, error: "A staff user with this email address already exists." };
    }
    const computedHash = hashPassword(data.password, "bcrypt");
    const newUser = {
      id: `usr-${Date.now()}`,
      name: data.name.trim(),
      email: normalizedEmail,
      role: data.role || "editor",
      password_hash: computedHash,
      hash_algorithm: "bcrypt",
      status: "locked",
      created_at: (/* @__PURE__ */ new Date()).toISOString().replace("T", " ").substring(0, 19),
      avatar: "/assets/images/uploads/madrasah_campus_hero_1790844614157.jpg"
    };
    inMemoryUsers.push(newUser);
    apiPost("/api/users", newUser);
    return { success: true, user: newUser };
  },
  setCurrentUser(user) {
    if (user) {
      safeSet(STORAGE_KEYS.AUTH, user);
      const found = inMemoryUsers.find((u) => u.id === user.id || u.email.toLowerCase() === user.email.toLowerCase());
      if (found) {
        found.last_login = (/* @__PURE__ */ new Date()).toISOString().replace("T", " ").substring(0, 19);
        apiPost("/api/users", found);
      }
    } else {
      localStorage.removeItem(STORAGE_KEYS.AUTH);
    }
  },
  // Language Preference
  getLanguage() {
    return safeGet(STORAGE_KEYS.LANG, "bn");
  },
  setLanguage(lang) {
    safeSet(STORAGE_KEYS.LANG, lang);
  }
};
